/**
 * Area and corner ops (TD §5): add, delete, species, count, lock, corner editing, and the zoo boundary.
 * Walls are structural: a corner inserted on a shared wall goes into every ring along that wall.
 */
import type { AnimalAreaKind, AreaId, OtherAreaKind, PlanDoc, Pt, SpeciesId, VertId } from '../../shared/types';
import { clamp, dist, distSeg } from '../geom/basic';
import { ringPoints } from '../geom/shapes';
import { isAnimalArea, MIN_RING_VERTS, poolsOf, ringIds, ringsUsingVert, whyCantRemoveVert, type RingRef } from '../model/query';
import { ok, refuse, type OpResult } from './result';
import { addVert, clampToMap, dedupeConsecutive, freshId, pruneOrphans } from './util';

/** Largest animal count per area (plan schema limit). */
export const MAX_COUNT = 500;
/** Animal count of a newly drawn habitat (the prototype's default). */
export const DEFAULT_COUNT = 2;

/** Arguments of addArea: an animal area needs a species; a pool may name the habitat it belongs to. */
export type AddAreaArgs =
  | { points: readonly Pt[]; kind: AnimalAreaKind; speciesId: SpeciesId; count?: number; locked?: boolean }
  | { points: readonly Pt[]; kind: OtherAreaKind; parent?: AreaId };

/** Distinct consecutive points of a ring (closer than 1 cm counts as the same). */
function ringDistinct(points: readonly Pt[]): Pt[] {
  const out = points.filter((p, i) => i === 0 || dist(p, points[i - 1] as Pt) > 0.01);
  while (out.length > 1 && dist(out[0] as Pt, out[out.length - 1] as Pt) <= 0.01) out.pop();
  return out;
}

/** Adds an area with its own new corners. Refuses fewer than 3 distinct points, or a pool whose parent isn't an animal area. */
export function addArea(draft: PlanDoc, args: AddAreaArgs): OpResult<AreaId> {
  const pts = ringDistinct(args.points);
  if (pts.length < MIN_RING_VERTS) return refuse('An area needs at least 3 corners.');
  if ('parent' in args && args.parent !== undefined) {
    const p = draft.areas[args.parent];
    if (!p || !isAnimalArea(p)) return refuse('A pool must belong to a habitat.');
  }
  const verts = pts.map(p => { const [x, y] = clampToMap(draft, p[0], p[1]); return addVert(draft, x, y); });
  const id = freshId(draft.areas);
  if (args.kind === 'habitat' || args.kind === 'aviary' || args.kind === 'aquarium') {
    const a = args as Extract<AddAreaArgs, { kind: AnimalAreaKind }>;
    draft.areas[id] = { kind: a.kind, verts, speciesId: a.speciesId, count: clamp(Math.round(a.count ?? DEFAULT_COUNT), 1, MAX_COUNT), locked: !!a.locked };
  } else {
    const a = args as Extract<AddAreaArgs, { kind: OtherAreaKind }>;
    draft.areas[id] = a.parent !== undefined ? { kind: a.kind, verts, parent: a.parent } : { kind: a.kind, verts };
  }
  return ok(id);
}

/** Deletes an area and the pools that belong to it. Shared corners stay for the neighbours; unused ones go. */
export function deleteArea(draft: PlanDoc, args: { areaId: AreaId }): OpResult {
  if (!draft.areas[args.areaId]) return refuse('That area no longer exists.');
  for (const pid of poolsOf(draft, args.areaId)) delete draft.areas[pid];
  delete draft.areas[args.areaId];
  pruneOrphans(draft);
  return ok(true);
}

/** Sets the species of an animal area. */
export function setSpecies(draft: PlanDoc, args: { areaId: AreaId; speciesId: SpeciesId }): OpResult {
  const a = draft.areas[args.areaId];
  if (!a || !isAnimalArea(a)) return refuse('Only habitats, aviaries and aquariums have animals.');
  a.speciesId = args.speciesId;
  return ok(true);
}

/** Sets the number of animals (rounded, 1–500). */
export function setCount(draft: PlanDoc, args: { areaId: AreaId; count: number }): OpResult {
  const a = draft.areas[args.areaId];
  if (!a || !isAnimalArea(a)) return refuse('Only habitats, aviaries and aquariums have animals.');
  a.count = clamp(Math.round(args.count), 1, MAX_COUNT);
  return ok(true);
}

/** Locks or unlocks an animal area. Locked areas are left alone by follow, carve and re-arrange. */
export function lockArea(draft: PlanDoc, args: { areaId: AreaId; locked: boolean }): OpResult {
  const a = draft.areas[args.areaId];
  if (!a || !isAnimalArea(a)) return refuse('Only habitats, aviaries and aquariums can be locked.');
  a.locked = args.locked;
  return ok(true);
}

/** Pins or unpins a corner: a pinned corner stays sharp when the ring is smoothed. */
export function setPinned(draft: PlanDoc, args: { vertId: VertId; pinned: boolean }): void {
  const v = draft.verts[args.vertId];
  if (!v) return;
  if (args.pinned) v.pinned = true; else delete v.pinned;
}

const splicedAt = (ids: readonly VertId[], at: number, w: VertId): VertId[] => [...ids.slice(0, at), w, ...ids.slice(at)];

/** Inserts `w` between every adjacent u–v pair (either direction, including the wrap-around) of a ring. */
function insertBetween(ids: VertId[], u: VertId, v: VertId, w: VertId): VertId[] {
  const out: VertId[] = [], n = ids.length;
  for (let i = 0; i < n; i++) {
    const a = ids[i] as VertId, b = ids[(i + 1) % n] as VertId;
    out.push(a);
    if ((a === u && b === v) || (a === v && b === u)) out.push(w);
  }
  return out;
}

/**
 * Inserts a corner at (x, y) into the ring's nearest edge. If that edge is a shared wall, the corner goes into
 * every ring along it, so the wall stays shared.
 */
export function insertVert(draft: PlanDoc, args: { ring: RingRef; x: number; y: number }): OpResult<VertId> {
  const ids = ringIds(draft, args.ring);
  if (!ids || ids.length < 2) return refuse('That shape no longer exists.');
  const pts = ringPoints(draft, ids), n = ids.length;
  let bi = 0, bd = Infinity;
  for (let i = 0; i < n; i++) {
    const d = distSeg(args.x, args.y, pts[i] as Pt, pts[(i + 1) % n] as Pt);
    if (d < bd) { bd = d; bi = i; }
  }
  const u = ids[bi] as VertId, v = ids[(bi + 1) % n] as VertId;
  const [x, y] = clampToMap(draft, args.x, args.y);
  const w = addVert(draft, x, y);
  const self = args.ring;
  // Only rings that change are written, so untouched areas keep their identity (derive cache, small patches).
  if (draft.plot) {
    const next = self === 'plot' ? splicedAt(draft.plot.verts, bi + 1, w) : insertBetween(draft.plot.verts, u, v, w);
    if (next.length !== draft.plot.verts.length) draft.plot.verts = next;
  }
  for (const [aid, a] of Object.entries(draft.areas)) {
    const next = self !== 'plot' && self.area === aid ? splicedAt(a.verts, bi + 1, w) : insertBetween(a.verts, u, v, w);
    if (next.length !== a.verts.length) a.verts = next;
  }
  return ok(w);
}

/** Removes a corner from every ring that uses it. Refuses if any ring would keep fewer than 3 corners (R12). */
export function removeVert(draft: PlanDoc, args: { vertId: VertId }): OpResult {
  const why = whyCantRemoveVert(draft, args.vertId);
  if (why) return refuse(why);
  for (const ring of ringsUsingVert(draft, args.vertId)) {
    const list = ringIds(draft, ring);
    if (!list) continue;
    const next = dedupeConsecutive(list.filter(v => v !== args.vertId), true);
    if (ring === 'plot') { if (draft.plot) draft.plot.verts = next; }
    else { const a = draft.areas[ring.area]; if (a) a.verts = next; }
  }
  delete draft.verts[args.vertId];
  return ok(true);
}

/** Replaces the zoo boundary with a new ring of its own corners, or removes it (null). */
export function setPlot(draft: PlanDoc, args: { points: readonly Pt[] | null; smooth?: boolean }): OpResult {
  if (args.points === null) { draft.plot = null; pruneOrphans(draft); return ok(true); }
  const pts = ringDistinct(args.points);
  if (pts.length < MIN_RING_VERTS) return refuse('The boundary needs at least 3 corners.');
  const verts = pts.map(p => { const [x, y] = clampToMap(draft, p[0], p[1]); return addVert(draft, x, y); });
  draft.plot = { verts, smooth: args.smooth ?? true };
  pruneOrphans(draft);
  return ok(true);
}

/** Switches the boundary between smoothed and straight edges. */
export function setPlotSmooth(draft: PlanDoc, args: { smooth: boolean }): void {
  if (draft.plot) draft.plot.smooth = args.smooth;
}
